/**
 * 4 KPI cards — apps total, healthy, accesses 24h, certs <30d.
 *
 * Cards são clicáveis (callback onFilterClick) — futuras integrações com
 * FiltersToolbar. Por enquanto, click é no-op visual.
 *
 * Visual: border-left colorido por categoria (teal/green/amber/red),
 * value em JetBrains Mono, label uppercase.
 */
import { Skeleton, SimpleGrid } from '@mantine/core'
import type { DashboardKpis } from '../types'
import styles from '../ZtnaAdmin.module.css'

export type KpiKey = 'apps_total' | 'healthy' | 'accesses' | 'certs' | 'disabled'

interface Props {
  data?: DashboardKpis
  isLoading?: boolean
  onFilterClick?: (kpi: KpiKey) => void
}

interface KpiCardSpec {
  key: KpiKey
  label: string
  value: string
  delta: string
  variant?: 'success' | 'warning' | 'danger'
}

export function KpiCards({ data, isLoading, onFilterClick }: Props) {
  if (isLoading || !data) {
    return (
      <SimpleGrid cols={{ base: 1, xs: 2, md: 3, lg: 5 }} spacing="md">
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} height={86} radius="md" />
        ))}
      </SimpleGrid>
    )
  }

  // Sempre usar data.apps_total como denominador (vem da mesma fetch
  // que data.healthy_count, garante sincronia). Antes usavamos
  // apps.length do query separado, que dessincronizava com kpis quando
  // user pausava um app (cache invalidado em uma query e nao na outra)
  // gerando bugs tipo "120% healthy".
  const total = data.apps_total
  // Defense in depth: clamp pra nunca exibir % > 100.
  const healthyClamped = Math.min(data.healthy_count, total)
  const healthyPct = total > 0 ? Math.round((healthyClamped / total) * 100) : 0
  const degraded = Math.max(0, total - healthyClamped)

  const specs: KpiCardSpec[] = [
    {
      key: 'apps_total',
      label: 'Apps cadastrados',
      value: String(data.apps_total),
      delta: `${data.apps_total} app${data.apps_total === 1 ? '' : 's'} ativo${data.apps_total === 1 ? '' : 's'}`,
    },
    {
      key: 'healthy',
      label: 'Health',
      value: `${healthyClamped}/${total}`,
      delta: `${healthyPct}% healthy · ${degraded} degraded/idle`,
      variant: 'success',
    },
    {
      key: 'accesses',
      label: 'Acessos 24h',
      value: String(data.accesses_24h),
      delta: data.accesses_24h === 0 ? 'Sem acessos registrados' : 'últimas 24h',
      variant: 'warning',
    },
    {
      key: 'certs',
      label: 'Cert expira <30d',
      value: String(data.certs_expiring_30d),
      delta: data.certs_expiring_30d === 0 ? 'Tudo certo' : 'Renovar urgência',
      variant: 'danger',
    },
    {
      key: 'disabled',
      label: 'Suspensas',
      value: String(data.apps_disabled ?? 0),
      delta: (data.apps_disabled ?? 0) === 0
        ? 'Nenhuma pausada'
        : 'Click pra mostrar e reativar',
      // Sem variant quando 0 (neutro/cinza); warning quando ha suspensas
      variant: (data.apps_disabled ?? 0) > 0 ? 'warning' : undefined,
    },
  ]

  return (
    <SimpleGrid cols={{ base: 1, xs: 2, md: 3, lg: 5 }} spacing="md">
      {specs.map((s) => {
        const variantClass = s.variant ? styles[s.variant] : ''
        return (
          <button
            key={s.key}
            type="button"
            className={`${styles.kpiCard} ${variantClass}`}
            onClick={() => onFilterClick?.(s.key)}
            aria-label={`${s.label}: ${s.value}`}
          >
            <span className={styles.kpiLabel}>{s.label}</span>
            <span className={styles.kpiValue}>{s.value}</span>
            <span className={styles.kpiDelta}>{s.delta}</span>
          </button>
        )
      })}
    </SimpleGrid>
  )
}

export default KpiCards
